import { useEffect, useState } from "react"
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { fetchWithAuth } from "@/lib/api/client"

interface EstadoData {
  estado: string
  cantidad: number
}

// Colores consistentes por estado
const ESTADO_COLORS: Record<string, string> = {
  BORRADOR: "hsl(220 13% 69%)", // Gris
  EN_ANALISIS: "hsl(210 100% 60%)", // Azul
  EN_PREPARACION: "hsl(45 93% 47%)", // Amarillo/Naranja
  PRESENTADA: "hsl(142 76% 36%)", // Verde oscuro
  EN_EVALUACION: "hsl(30 100% 60%)", // Naranja
  ADJUDICADA: "hsl(142 76% 36%)", // Verde (éxito)
  RECHAZADA: "hsl(0 84% 60%)", // Rojo
  DESIERTA: "hsl(0 0% 50%)", // Gris oscuro
  CANCELADA: "hsl(0 0% 30%)", // Gris muy oscuro
}

// Etiquetas legibles en español
const ESTADO_LABELS: Record<string, string> = {
  BORRADOR: "Borrador",
  EN_ANALISIS: "En Análisis",
  EN_PREPARACION: "En Preparación",
  PRESENTADA: "Presentada",
  EN_EVALUACION: "En Evaluación",
  ADJUDICADA: "Adjudicada",
  RECHAZADA: "Rechazada",
  DESIERTA: "Desierta",
  CANCELADA: "Cancelada",
}

export function ChartEstadoPie() {
  const [data, setData] = useState<EstadoData[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        const result = await fetchWithAuth(
          `/api/v1/analytics/licitaciones/estado`
        )
        setData(result)
      } catch (error) {
        console.error("Error fetching licitaciones por estado:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  if (loading) {
    return <ChartEstadoPieLoading />
  }

  // Preparar datos para el gráfico con etiquetas y colores
  const chartData = data.map((item) => ({
    name: ESTADO_LABELS[item.estado] || item.estado,
    value: item.cantidad,
    estado: item.estado,
  }))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Distribución por Estado</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }: { name?: string; percent?: number }) =>
                `${name ?? ''}: ${((percent ?? 0) * 100).toFixed(0)}%`
              }
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={ESTADO_COLORS[entry.estado] || "hsl(var(--muted))"}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "var(--radius)",
              }}
              itemStyle={{
                color: "hsl(var(--foreground))",
              }}
              formatter={(value) => [`${value} licitaciones`, "Cantidad"]}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              wrapperStyle={{
                paddingTop: "20px",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function ChartEstadoPieLoading() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[350px] w-full" />
      </CardContent>
    </Card>
  )
}
